import React, { useEffect, useState } from 'react';
import { Provider } from 'react-redux';
// routes
import Router from './routes';
// theme
import ThemeConfig from './theme';
// components
import 'devextreme/dist/css/dx.light.css'; // TODO: remove it 
import '@devexpress/dx-react-grid-bootstrap4/dist/dx-react-grid-bootstrap4.css';
// redux
import { store, useDispatch } from './redux/store';
import StyledCircularProgress from './components/StyledCircularProgress';
import { ErrorBoundary } from './components/ErrorBoundary';
import { getAppSettingsAsync } from './Common/api-clients/settingsClient';
import { getUserInformationAsync } from './Common/api-clients/userInformationClient';
import { UpdateUserInfoActionPayload } from './Common/Reducer/userInfoActionAnReducerTypes';
import { updateUserInformation, initalizeFetchUserInformation, endFetchUserInformation } from './Common/Reducer/userInfoActionsAndReducer';
import { getSelectedClient, saveAppSettings } from './Common/Utils/localStorageHelper';
import NotistackProvider from './components/NotistackProvider';
import RtlLayout from './components/RtlLayout';
import ThemePrimaryColor from './components/ThemePrimaryColor';
import CacheBuster from './Common/Utils/CacheBuster';
import ThemeLocalization from './components/ThemeLocalization';
import { getBackendDataListIDM, getBackendDataListAzureB2C, setClientOptions } from './Common/Utils/backendDataHelper';
import { useNavigate } from 'react-router';
import withButtonTracking from './hooks/useButtonTracking';
import { appInsights, hasSentDailyLog, updateLastSentDate } from './hooks/useAppinsight';
import { AuthMethod } from './Common/enums';
import { BackendData } from './utils/backendDataParser';
import { useSnackbar } from 'notistack';
import { AppSettings } from './@types/AppSettings';
import { getUserAsync, logout } from './Common/api-clients/authClient';
import { setUserState } from './Common/Reducer/userReducer';
import useLocales from './hooks/useLocales';

// ----------------------------------------------------------------------

const StatefulApp: React.FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { translate } = useLocales();

  const [appSettings, setAppSettings] = useState<AppSettings>();
  // const [userManager, setUserManager] = useState<UserManager>();
  const [userFinishedLoading, setUserFinishedLoading] = useState(false);

  useEffect(() => {
    // If a previous session detected an admin-bypass attempt (authenticated with no client
    // mapping) and signed the user out, route them to /admin so they can supply a client ID
    // via the proper flow — instead of letting AuthGuard send them back through the default
    // login page, which would just reproduce the same broken state.
    if (localStorage.getItem('adminFlowRequired') === 'true') {
      localStorage.removeItem('adminFlowRequired');
      if (window.location.pathname !== '/admin') {
        window.location.replace('/admin');
        return;
      }
    }

    getAppSettingsAsync()
      .then(settings => {
        setAppSettings(settings);
      }).catch((error: Error) => {
        enqueueSnackbar(error.message, { variant: 'error' });
      })
  }, []);


  useEffect(() => {
    if (appSettings) {
      saveAppSettings(appSettings);
    }
  }, [appSettings]);

  useEffect(() => {

    if (!userFinishedLoading && appSettings) {
      dispatch(initalizeFetchUserInformation());
      getUserAsync()
        .then(async (reloadedUser) => {
          dispatch(setUserState(reloadedUser));
          if (!reloadedUser.isAuthenticated) {
            console.log('App | Reloaded user is not authenticated.');

            setUserFinishedLoading(true);
            dispatch(endFetchUserInformation());
          } else {
            console.log('App | User is authenticated.');

            const isAdmin = sessionStorage.getItem('isAdmin') === 'true';
            let selectedClient: BackendData | undefined = undefined;

            if (isAdmin) {
              //Admin users don't have BackendDataList in the clientEmail table
              //No need to call api, backend is set during Admin login process
              selectedClient = getSelectedClient();
            } else {
              let backendDataList = [];
              if (appSettings?.authMethod === AuthMethod.IDM) {
                backendDataList = getBackendDataListIDM(reloadedUser);
              } else {
                backendDataList = await getBackendDataListAzureB2C();
              }

              if (backendDataList.length === 0) {
                // Authenticated but no client mapping exists. Treat as a customer-admin who
                // bypassed `/admin`: clear any stale state, raise the `adminFlowRequired` flag
                // (read by the first effect on the next page load to bounce the user to /admin
                // instead of the default login flow) and sign them out after 5s.
                sessionStorage.removeItem('isAdmin');
                localStorage.removeItem('clientOptions');
                localStorage.removeItem('selectedClient');
                localStorage.setItem('adminFlowRequired', 'true');
                enqueueSnackbar(
                  translate('Login.NoClientIdFound'),
                  { variant: 'error', autoHideDuration: 5000 }
                );
                setTimeout(() => logout(), 5000);
                return;
              }

              selectedClient = setClientOptions(backendDataList);
            }

            if (!hasSentDailyLog()) {
              const eventData = {
                name: 'UserLogin',
                properties: {
                  application: 'Connect',
                  client: selectedClient?.name,
                  user: reloadedUser.profile.sub,
                }
              };
              console.log('Sending daily use count event:', eventData); // Debug log
              appInsights.trackEvent(eventData);
              updateLastSentDate();
            }

            if (!selectedClient) {
              console.log('App | Selected Client is undefined.');

              setUserFinishedLoading(true);
              dispatch(endFetchUserInformation());
            } else {
              getUserInformationAsync(selectedClient.name)
                .then(result => {
                  const payload: UpdateUserInfoActionPayload = {
                    userInformation: {
                      userPermissions: result.userPermissions,
                      userSettings: result.userSettings,
                      license: result.license,
                      isCustomerPortalSuperAdmin: result.isCustomerPortalSuperAdmin,
                      employeeId: result.employeeId,
                      identityId: result.identityId,
                      name: result.name
                    }
                  };

                  dispatch(updateUserInformation(payload));
                  console.log('App | User information update dispatched.');
                })
                .catch((error) => {
                  console.error('Error fetching user information:', error);
                  dispatch(endFetchUserInformation());
                  navigate('/error');
                })
                .finally(() => {
                  setUserFinishedLoading(true);
                });
            }
          }
        })
        .catch((error) => {
          console.error('Error loading user:', error);
          setUserFinishedLoading(true);
          dispatch(endFetchUserInformation());
        });
    }
  }, [appSettings]);


  return (
    <>
      {
        userFinishedLoading === true ?
          /* @ts-ignore */ /* ignore needed because in react 18.x children prop was removed */

          <ErrorBoundary>
            {/* <Settings /> */}
            <Router />
          </ErrorBoundary>
          :
          <StyledCircularProgress />
      }
    </>
  );
};

const TrackedApp = withButtonTracking(StatefulApp);

export default function App() {
  return (
    <CacheBuster>
      {({ loading, isLatestVersion, refreshCacheAndReload }) => {
        if (loading) return null;
        // Clear cache and reload page if newer version arrived
        if (!loading && !isLatestVersion) { refreshCacheAndReload(); }
        return (
          <div style={{
            paddingTop: `env(safe-area-inset-top)`,
            paddingBottom: `env(safe-area-inset-bottom)`
          }}>
            <Provider store={store}>
              <ThemeConfig>
                <ThemePrimaryColor>
                  <ThemeLocalization>
                    <RtlLayout>
                      <NotistackProvider>
                        <TrackedApp />
                      </NotistackProvider>
                    </RtlLayout>
                  </ThemeLocalization>
                </ThemePrimaryColor>
              </ThemeConfig>
            </Provider>
          </div>
        );
      }}
    </CacheBuster>
  );
}
